import React from "react";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * DEV-015D · Global inactivity warning modal (Web).
 *
 * Mounted at the AuthProvider level so it wraps every authenticated
 * route without each shell having to opt-in. Disabled entirely when
 * the user is signed out so the login screen isn't spammed with
 * modal state.
 *
 * Countdown displayed in the modal is derived from the SAME idle
 * timer that decides when to sign the user out — they can't drift.
 */
export function IdleWarningModal() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const onLogout = React.useCallback(async () => {
        try { await logout(); } catch { /* ignore */ }
        try {
            toast.info("You were signed out after 30 minutes of inactivity.", {
                duration: 8000,
            });
        } catch { /* ignore */ }
        // Route hard to landing so back-nav can't restore the app.
        navigate("/", { replace: true });
    }, [logout, navigate]);

    const { warning, remainingMs, stayActive } = useIdleTimeout({
        enabled: Boolean(user),
        onLogout,
    });

    if (!warning) return null;

    const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
    const mm = Math.floor(secondsLeft / 60).toString().padStart(1, "0");
    const ss = (secondsLeft % 60).toString().padStart(2, "0");

    return (
        <div
            role="alertdialog"
            aria-live="assertive"
            aria-labelledby="idle-warning-title"
            aria-describedby="idle-warning-body"
            data-testid="idle-warning-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
        >
            <div className="bg-white rounded-xl shadow-2xl border border-[#E5E7EB] max-w-sm w-full p-6">
                <h2 id="idle-warning-title" className="text-lg font-semibold text-[#131B2F]">
                    You'll be signed out soon due to inactivity
                </h2>
                <p id="idle-warning-body" className="mt-2 text-sm text-[#4B5565]">
                    For your security, we'll sign you out in{" "}
                    <span data-testid="idle-warning-countdown">{mm}:{ss}</span>.
                </p>
                <div className="mt-5 flex gap-3">
                    <button
                        type="button"
                        data-testid="idle-warning-stay"
                        onClick={stayActive}
                        aria-label="Stay signed in"
                        className="flex-1 h-11 rounded-md bg-[#3E9E2E] hover:bg-[#2E7E22] text-white font-medium"
                    >
                        Stay signed in
                    </button>
                    <button
                        type="button"
                        data-testid="idle-warning-signout"
                        onClick={onLogout}
                        aria-label="Sign out"
                        className="flex-1 h-11 rounded-md border border-[#E5E7EB] bg-white text-[#131B2F] font-medium hover:bg-[#F5F5F7]"
                    >
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}
